import { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const grades = ['Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

const LeadForm = ({ variant = 'light' }) => {
  const [form, setForm] = useState({ name: '', phone: '', grade: '', service: 'General Inquiry', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.grade) return;
    setLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setSuccess(true);
      setForm({ name: '', phone: '', grade: '', service: 'General Inquiry', message: '' });
      setTimeout(() => setSuccess(false), 4000);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === 'dark';
  const inputClass = isDark
    ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F2C402]'
    : 'bg-white border-[#121212] text-[#121212] placeholder:text-[#52525B] focus:border-[#F2C402]';

  return (
    <div data-testid="lead-form-container" className={`rounded-2xl p-6 md:p-8 ${isDark ? 'bg-white/5 border border-white/10' : 'border-2 border-[#121212] bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'}`}>
      <h3 className={`font-sans font-bold text-xl mb-2 ${isDark ? 'text-white' : 'text-[#121212]'}`}>
        Get in Touch
      </h3>
      <p className={`text-sm font-body mb-6 ${isDark ? 'text-white/50' : 'text-[#52525B]'}`}>
        Fill in your details and we'll reach out to you.
      </p>

      <AnimatePresence mode="wait">
        {success ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-8 text-center"
          >
            <CheckCircle2 className="w-12 h-12 text-[#25D366] mb-3" />
            <p className={`font-sans font-bold text-lg ${isDark ? 'text-white' : 'text-[#121212]'}`}>Thank you!</p>
            <p className={`text-sm font-body ${isDark ? 'text-white/50' : 'text-[#52525B]'}`}>We'll get back to you shortly.</p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="lead-form"
          >
            <input
              data-testid="lead-form-name"
              type="text"
              placeholder="Your Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-body outline-none transition-colors ${inputClass}`}
            />
            <input
              data-testid="lead-form-phone"
              type="tel"
              placeholder="Phone Number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
              className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-body outline-none transition-colors ${inputClass}`}
            />
            <select
              data-testid="lead-form-grade"
              value={form.grade}
              onChange={(e) => setForm({ ...form, grade: e.target.value })}
              required
              className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-body outline-none transition-colors ${inputClass}`}
            >
              <option value="">Select Grade</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <button
              data-testid="lead-form-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#F2C402] text-[#0A0A0A] px-6 py-3.5 rounded-xl font-bold text-sm hover:bg-[#E9811F] hover:text-white transition-all disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {loading ? 'Sending...' : 'Send Enquiry'}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadForm;
